import React from 'react';
import { motion } from 'framer-motion';

function FinancialPlanning() {
  const focusAreas = [
    {
      title: 'Cash‑Flow & Budgeting',
      points: ['Spending analysis', 'Savings plans', 'Emergency fund sizing', 'Debt optimization'],
    },
    {
      title: 'Goal Planning',
      points: ['Education funding', 'Major purchases', 'Lifestyle milestones', 'Timeline mapping'],
    },
    {
      title: 'Protection Planning',
      points: ['Insurance audits', 'Income protection', 'Estate basics', 'Risk coverage alignment'],
    },
    {
      title: 'Investment Alignment',
      points: ['Objectives & constraints', 'Risk tolerance calibration', 'Asset mix guidance', 'Review cadence'],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative h-[55vh] overflow-hidden">
        <img src="/new-assets/pexels-felicity-tai-7964412.jpg" alt="Financial planning" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/80" />
        <div className="relative z-10 flex h-full items-center justify-center text-center px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="text-white text-5xl font-light mb-4">Financial <span className="font-semibold text-[#fbbf24]">Planning</span></h1>
            <p className="text-white/90 text-xl max-w-3xl">Expert guidance to turn long‑term ambitions into clear, actionable plans.</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div data-aos="fade-up" className="text-center mb-14">
            <h2 className="text-3xl font-semibold text-gray-900">Plan with Confidence</h2>
            <p className="text-gray-600 text-lg mt-2">Structured planning across cash‑flow, goals, protection and investments.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {focusAreas.map((area, i) => (
              <div key={area.title} data-aos="fade-up" data-aos-delay={`${150 + i * 100}`} className="p-8 bg-white rounded-2xl shadow-lg border border-gray-100">
                <div className="w-12 h-12 rounded-full bg-[#0056d7] flex items-center justify-center text-white font-semibold mb-5">{String(i+1).padStart(2,'0')}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{area.title}</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  {area.points.map(p => (
                    <li key={p} className="flex items-start gap-2"><span className="mt-1 w-2 h-2 rounded-full bg-[#0056d7]" />{p}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-14 text-center">
            <motion.a href="/contact" className="inline-block px-10 py-4 rounded-full bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-black font-medium shadow-lg hover:shadow-amber-500/30 transition-colors" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.96 }}>
              Speak with a Planner
            </motion.a>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-6">
          <div data-aos="fade-up" className="text-center mb-14">
            <h2 className="text-3xl font-semibold text-gray-900">Why Clients Choose Us</h2>
            <p className="text-gray-600 text-lg mt-2">Independent advice, rigorous process, and transparent reporting.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[ 
              { t:'Independent & Fiduciary', d:'We sit on your side of the table—no product bias.' },
              { t:'Holistic Coordination', d:'We coordinate investments, taxes, insurance and estate.' },
              { t:'Actionable Roadmaps', d:'Clear next steps with timelines and ownership.' },
              { t:'Ongoing Accountability', d:'Quarterly reviews and fast answers when life changes.' },
            ].map((b,i)=> (
              <div key={b.t} data-aos="fade-up" data-aos-delay={`${150+i*100}`} className="p-8 bg-white rounded-2xl shadow-lg border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{b.t}</h3>
                <p className="text-gray-600">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div data-aos="fade-up" className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-gray-900">Financial Planning FAQs</h2>
          </div>
          <div className="space-y-6">
            {[
              {q:'How do you charge for planning?', a:'Flat planning fees for the plan, and transparent AUM fees for ongoing portfolio management, with no hidden commissions.'},
              {q:'How often do we meet?', a:'Initial deep‑dive, a 60‑day follow‑up, then quarterly reviews or as life events occur.'},
              {q:'Can you work with my CPA/attorney?', a:'Yes. We regularly coordinate with outside professionals to align tax and estate considerations with your plan.'}
            ].map((f,i)=> (
              <div key={f.q} data-aos="fade-up" data-aos-delay={`${100+i*100}`} className="p-6 rounded-xl bg-gray-50 border border-gray-100">
                <div className="font-semibold text-gray-900 mb-1">{f.q}</div>
                <div className="text-gray-700">{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default FinancialPlanning;


